import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Users, UserPlus, Sparkles, Search, Lock, ShieldCheck, CheckCircle2,
  Building2, MapPin, GraduationCap, Target, Clock3, X, AlertTriangle, Printer, FileText, Briefcase, ClipboardList,
} from "lucide-react";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("ar-SA"); } catch { return "—"; } };


export default function AssociationStudents() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [detailId, setDetailId] = useState(null);



  const load = async () => {
    setLoading(true);
    try {
      const dir = await api.get("/association/students-directory?placed=true");
      setStudents(dir.data.students || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return students;
    return students.filter((s) => [s.name, s.city, s.university, s.major].some((f) => (f || "").toLowerCase().includes(t)));
  }, [students, q]);


  return (
    <DashboardLayout>
      <div data-testid="assoc-students-directory">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-teal-700">
              الطلاب الموزَّعون
            </div>
            <h1 className="mt-2 text-2xl font-heading font-black text-slate-900">
              طلاب جمعيتكم النشِطون في التدريب
            </h1>
            <p className="mt-2 text-slate-600 max-w-3xl">
              قائمة الطلاب الذين سكّنتهم إدارة المنصّة لدى جمعيتكم. اضغط على أي طالب لعرض ملفه الكامل ومتابعة تدريبه.
            </p>
          </div>
        </div>


        <div className="mb-5 flex flex-wrap items-center gap-2">

          <div className="flex-1 min-w-[220px] p-2 bg-white rounded-xl border border-cream-200 flex items-center gap-2">
            <Search className="h-4 w-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث بالاسم، المدينة، التخصص" className="flex-1 bg-transparent text-sm focus:outline-none" data-testid="students-search" />
          </div>
        </div>

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-10 rounded-2xl border border-dashed border-slate-300 bg-white text-center text-slate-500">
            {students.length === 0 ? "لا يوجد طلاب مطابقون." : "لا نتائج للبحث."}
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="students-grid">
            {filtered.map((s) => (
              <StudentCard
                key={s.id}
                s={s}
                onOpen={() => setDetailId(s.id)}
              />
            ))}
          </div>
        )}

        {detailId && (
          <StudentProfileModal
            studentId={detailId}
            onClose={() => setDetailId(null)}
          />
        )}
      </div>
    </DashboardLayout>
  );
}



function StudentCard({ s, onOpen }) {
  const p = s.placement;
  return (
    <div className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow hover:-translate-y-0.5 hover:shadow-xl transition-all cursor-pointer" onClick={onOpen} data-testid={`stu-card-${s.id}`}>
      <div className="flex items-start gap-3">
        <div className="h-12 w-12 rounded-full bg-gradient-to-br from-teal-700 to-teal-900 grid place-items-center text-white font-heading font-black">{s.name?.[0] || "؟"}</div>
        <div className="min-w-0 flex-1">
          <div className="font-heading font-black text-slate-900 truncate">{s.name}</div>
          <div className="text-xs text-slate-500 mt-0.5 truncate">{[s.major, s.university].filter(Boolean).join(" · ") || "بيانات دراسية غير مكتملة"}</div>
        </div>
        <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">مُسكَّن</span>
      </div>

      {/* Placement details — placed students only */}
      {s.placed && p && (
        <div className="mt-3 p-3 rounded-xl bg-emerald-50/60 border border-emerald-100" data-testid={`placement-info-${s.id}`}>
          <div className="flex items-start gap-2">
            <Briefcase className="h-4 w-4 text-emerald-700 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-xs font-bold text-emerald-800 truncate">{p.opportunity_title || "—"}</div>
              {p.program_name && (
                <div className="text-[11px] text-slate-600 truncate mt-0.5">{p.program_name}</div>
              )}
              <div className="mt-1.5 flex flex-wrap gap-2 text-[10px] text-slate-600">
                {p.category && <span className="inline-flex px-1.5 py-0.5 rounded-full bg-white border border-emerald-100 font-bold text-emerald-800">{p.category}</span>}
                {p.city && <span className="inline-flex items-center gap-0.5"><MapPin className="h-3 w-3" /> {p.city}</span>}
                {p.duration_weeks && <span className="inline-flex items-center gap-0.5 arabic-numerals"><Clock3 className="h-3 w-3" /> {p.duration_weeks} أسبوع</span>}
              </div>
              {(p.training_start || p.created_at) && (
                <div className="mt-1 text-[10px] text-slate-500 arabic-numerals">
                  {p.training_start
                    ? <>بداية التدريب: {fmtDate(p.training_start)}</>
                    : <>تاريخ التسكين: {fmtDate(p.created_at)}</>
                  }
                </div>
              )}
            </div>
          </div>
        </div>
      )}


      <div className="mt-3 flex items-center gap-2 justify-between">
        <span className="text-[11px] text-slate-500">
          {s.survey_completed ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3 w-3" /> استبيان مكتمل</span> : <span className="text-amber-700">استبيان غير مكتمل</span>}
        </span>
        {p?.application_id && (
          <Link to={`/dashboard/association/journeys/${p.application_id}`} onClick={(event) => event.stopPropagation()} className="inline-flex min-h-9 items-center rounded-lg bg-teal-700 px-3 text-xs font-bold text-white hover:bg-teal-600" data-testid={`open-journey-${s.id}`}>
            متابعة الرحلة
          </Link>
        )}
        {s.placed && p && !p.application_id && (
          <span className="inline-flex min-h-9 items-center rounded-lg bg-amber-50 px-3 text-xs font-bold text-amber-800 ring-1 ring-inset ring-amber-200" title="هذا تسكين قديم غير مرتبط بطلب فرصة؛ راجع إدارة المنصة لربطه" data-testid={`journey-link-missing-${s.id}`}>
            الرحلة تحتاج ربطًا
          </span>
        )}
      </div>
    </div>
  );
}

function NeedRow({ icon: Icon, label, value }) {
  if (!value) return null;
  const text = Array.isArray(value) ? value.join("، ") : String(value);
  return (
    <div className="inline-flex items-start gap-1 max-w-full">
      <Icon className="h-3.5 w-3.5 text-teal-700 mt-0.5 shrink-0" />
      <span className="min-w-0 truncate"><span className="text-slate-500">{label}: </span>{text}</span>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between items-baseline gap-2 border-b border-slate-100 pb-1.5">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="font-semibold text-slate-800 text-end">{value || "—"}</span>
    </div>
  );
}

function NeedBig({ label, value }) {
  if (!value) return (<div className="p-3 rounded-lg bg-slate-50 border border-slate-100 text-xs text-slate-400"><span className="font-bold text-slate-600">{label}: </span>لم يذكره الطالب</div>);
  const text = Array.isArray(value) ? value.join("، ") : String(value);
  return (
    <div className="p-3 rounded-lg bg-cream-50 border border-cream-200 text-sm">
      <div className="text-[11px] font-bold text-teal-700 mb-0.5">{label}</div>
      <div className="text-slate-800 whitespace-pre-line">{text}</div>
    </div>
  );
}

// -------------------- Full-profile modal (fetches complete survey) --------------------
function renderAnswer(v) {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "boolean") return v ? "نعم" : "لا";
  if (Array.isArray(v)) return v.filter(Boolean).join("، ");
  if (typeof v === "object") {
    try { return JSON.stringify(v); } catch { return String(v); }
  }
  return String(v);
}

function StudentProfileModal({ studentId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [filing, setFiling] = useState(false);
  const [complaint, setComplaint] = useState({ subject: "", body: "", priority: "medium" });
  const [savingC, setSavingC] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: r } = await api.get(`/association/student-full/${studentId}`);
        if (alive) setData(r);
      } catch (e) {
        if (alive) setErr(formatApiError(e));
      }
    })();
    return () => { alive = false; };
  }, [studentId]);


  const fileComplaint = async () => {
    if (!complaint.subject.trim() || !complaint.body.trim()) { toast.error("املأ الموضوع والوصف"); return; }
    setSavingC(true);
    try {
      await api.post("/complaints", { student_id: studentId, ...complaint });
      toast.success("تم رفع الشكوى — ستراها الإدارة مع الجهات المعنية");
      setComplaint({ subject: "", body: "", priority: "medium" });
      setFiling(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingC(false); }
  };

  const profile = data?.profile;
  const survey = data?.survey;
  const placement = data?.placement;
  const answers = survey?.answers || {};
  const labels = survey?.labels || {};
  const answerEntries = Object.keys(labels)
    .map((k) => ({ key: k, label: labels[k], val: renderAnswer(answers[k]) }))
    .filter((x) => x.val !== null);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-3xl max-h-[92vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-slate-200" data-testid="stu-full-profile-modal">
        <div className="sticky top-0 z-10 px-4 py-3 bg-gradient-to-l from-teal-700 to-teal-800 text-white flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs opacity-80">الملف التعريفي الكامل للطالب</div>
            <div className="mt-0.5 font-heading font-black text-lg truncate">{profile?.name || "…"}</div>
            {profile?.email && <div className="text-[11px] opacity-80 truncate">{profile.email}</div>}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.open(`/dashboard/students-directory/${studentId}/print`, "_blank")}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-bold"
              data-testid="print-profile-btn"
              title="فتح صفحة طباعة"
            >
              <Printer className="h-3.5 w-3.5" /> طباعة
            </button>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10"><X className="h-5 w-5" /></button>
          </div>
        </div>

        {!data && !err && (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        )}
        {err && (
          <div className="p-5 text-center text-rose-600 text-sm">{err}</div>
        )}

        {data && (
          <div className="p-4 space-y-6 text-sm">
            {/* Basic info */}
            <section>
              <SectionTitle icon={GraduationCap} title="المعلومات الأساسية" />
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
                <Row label="التخصص" value={profile?.major} />
                <Row label="الجامعة / الكلية" value={[profile?.university, profile?.college].filter(Boolean).join(" — ")} />
                <Row label="المدينة" value={profile?.city} />
                <Row label="المعدل" value={profile?.gpa} />
                <Row label="الجوال" value={profile?.phone} />
                <Row label="تاريخ التسجيل" value={fmtDate(profile?.registered_at)} />
              </div>
            </section>

            {/* Placement */}
            {placement && (
              <section>
                <SectionTitle icon={Briefcase} title="التسكين الحالي" tint="text-emerald-700" />
                <div className="p-4 rounded-xl bg-emerald-50/60 border border-emerald-100">
                  <div className="font-heading font-bold text-slate-900">{placement.opportunity_title}</div>
                  {placement.opportunity && (
                    <div className="mt-1 text-xs text-slate-600 flex flex-wrap gap-3">
                      {placement.opportunity.city && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {placement.opportunity.city}</span>}
                      {placement.opportunity.duration_weeks && <span className="arabic-numerals">{placement.opportunity.duration_weeks} أسبوع</span>}
                      {placement.opportunity.training_start && <span className="arabic-numerals">يبدأ {fmtDate(placement.opportunity.training_start)}</span>}
                    </div>
                  )}
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                    <Stat label="مهام كلية" value={placement.tasks?.total || 0} />
                    <Stat label="مكتملة" value={placement.tasks?.done || 0} tint="text-emerald-700" />
                    <Stat label="ساعات محتسبة" value={placement.tasks?.expected_hours || 0} tint="text-teal-700" />
                    <Stat label="تقارير أسبوعية" value={placement.reports_count || 0} />
                  </div>
                  {placement.progress && <div className="mt-4 space-y-3"><div className="text-xs font-bold text-slate-700">تفاصيل تقدم الإنجاز ({placement.progress.summary?.progress_percent || 0}%)</div><progress max="100" value={placement.progress.summary?.progress_percent || 0} className="w-full accent-teal-600" /><div className="grid sm:grid-cols-2 gap-2">{(placement.progress.tasks || []).map((t) => <div key={t.id} className="p-3 rounded-lg bg-white border border-emerald-100"><div className="font-bold">{t.title}</div><div className="text-xs text-slate-500 mt-1">{t.status === "done" ? "مكتملة" : t.status === "submitted" ? "بانتظار التقييم" : "مفتوحة"} • {t.expected_hours || 0} ساعة</div>{t.due_date && <div className="text-xs text-slate-500">الإنجاز المستهدف: {fmtDate(t.due_date)}</div>}{t.completed_at && <div className="text-xs text-emerald-700">تم الإنجاز: {fmtDate(t.completed_at)}</div>}</div>)}</div><div className="text-xs text-slate-600">الحضور: {placement.progress.summary?.present_days || 0} • الغياب: {placement.progress.summary?.absent_days || 0} • ساعات الحضور: {placement.progress.summary?.attendance_hours || 0}</div></div>}
                  <div className="mt-4"><div className="text-xs font-bold text-slate-700 mb-2">الشكاوى المرتبطة بالطالب</div>{(placement.complaints || []).length === 0 ? <div className="text-xs text-slate-500">لا توجد شكاوى مسجلة.</div> : <div className="space-y-2">{placement.complaints.map((c) => <div key={c.id} className="rounded-lg bg-white border border-rose-100 p-3"><div className="flex justify-between gap-2"><span className="font-bold text-slate-900">{c.subject}</span><span className="text-xs text-slate-500">{c.status}</span></div><div className="text-xs text-slate-700 mt-1 whitespace-pre-wrap">{c.body}</div><div className="text-[11px] text-slate-500 mt-1">رفعها: {c.filed_by_name}</div>{c.resolution_note && <div className="mt-2 text-xs bg-emerald-50 p-2 rounded">المعالجة: {c.resolution_note}</div>}</div>)}</div>}</div>
                </div>
              </section>
            )}

            {/* Full survey answers */}
            <section>
              <SectionTitle icon={ClipboardList} title="إجابات الاستبيان الأولي كاملة" />
              {!survey?.completed && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-3">
                  الطالب لم يُكمل استبيانه الأولي بعد.
                </div>
              )}
              {survey?.completed && (
                <>
                  {survey.submitted_at && (
                    <div className="text-[11px] text-slate-500 mb-3 arabic-numerals">
                      تم الإرسال: {fmtDate(survey.submitted_at)}
                    </div>
                  )}
                  {answerEntries.length === 0 ? (
                    <div className="text-xs text-slate-500">لا توجد إجابات مسجّلة.</div>
                  ) : (
                    <div className="grid sm:grid-cols-2 gap-2">
                      {answerEntries.map((e) => (
                        <div key={e.key} className="p-3 rounded-lg bg-cream-50 border border-cream-200">
                          <div className="text-[11px] font-bold text-teal-700 mb-0.5">{e.label}</div>
                          <div className="text-slate-800 text-sm whitespace-pre-line break-words">{e.val}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </section>

            {/* Complaint (placed only) */}
            {placement && (
              <section>
                <div className="flex items-center justify-between mb-2">
                  <SectionTitle icon={AlertTriangle} title="رفع شكوى" tint="text-rose-700" inline />
                  {!filing && (
                    <button onClick={() => setFiling(true)} className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 text-xs font-bold" data-testid="assoc-file-complaint">
                      رفع شكوى
                    </button>
                  )}
                </div>
                {filing && (
                  <div className="p-3 rounded-xl bg-rose-50/50 border border-rose-100 space-y-2" data-testid="assoc-complaint-form">
                    <input value={complaint.subject} onChange={(e) => setComplaint({ ...complaint, subject: e.target.value })} placeholder="الموضوع" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="c-subject" />
                    <select value={complaint.priority} onChange={(e) => setComplaint({ ...complaint, priority: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
                      <option value="low">عادية</option><option value="medium">متوسطة</option><option value="high">عالية</option>
                    </select>
                    <textarea rows={3} value={complaint.body} onChange={(e) => setComplaint({ ...complaint, body: e.target.value })} placeholder="تفاصيل الشكوى..." className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="c-body" />
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setFiling(false)} className="px-3 py-1.5 rounded-lg text-slate-700 hover:bg-slate-100 text-xs">إلغاء</button>
                      <button onClick={fileComplaint} disabled={savingC} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-rose-600 text-white text-xs font-bold hover:bg-rose-700 disabled:opacity-60" data-testid="c-submit">
                        {savingC ? <Loader2 className="h-3 w-3 animate-spin" /> : null} إرسال
                      </button>
                    </div>
                  </div>
                )}
              </section>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

function SectionTitle({ icon: Icon, title, tint = "text-teal-700", inline = false }) {
  return (
    <div className={`${inline ? "" : "mb-3"} flex items-center gap-2`}>
      {Icon && <Icon className={`h-4 w-4 ${tint}`} />}
      <h4 className={`font-heading font-black text-sm ${tint} uppercase tracking-widest`}>{title}</h4>
    </div>
  );
}

function Stat({ label, value, tint = "text-slate-800" }) {
  return (
    <div className="p-2 rounded-lg bg-white border border-emerald-100">
      <div className={`text-lg font-heading font-black arabic-numerals ${tint}`}>{value}</div>
      <div className="text-[10px] text-slate-500">{label}</div>
    </div>
  );
}
